// v0.6.10 — canned acknowledgement reasons, per flag.
//
// Operator: "where possible do dropdown to select from, no free
// writing boxes please."
//
// A free-text box asked every operator to invent the same sentence,
// and then to re-invent it on the next row. That is slow, and it
// produces an audit trail where the SAME decision is recorded in a
// dozen different wordings — which is worse for whoever reads it
// later, not better.
//
// Each preset is written to stand on its own in an audit trail months
// later: it says what was decided AND why that is the right treatment,
// not just "checked".
//
// "Other — type my own" was kept here as a last resort until A2-571.
// Operator, 2026-10-27: "We should not give options of free text only
// select dropdown." It is gone from every list and every select; a
// reference number (an identifier, not a sentence) is the only thing
// still typed, and only where one is genuinely needed (amendment notes).
// If a real situation is not covered, the fix is a new preset here.

const GENERIC = [
  "Reviewed against the source document — the current treatment is correct.",
  "Reviewed with the client — confirmed correct, keeping as-is.",
  "Known data-quality gap at source; treatment is still correct for this period.",
];

const BY_FLAG = {
  BUYER_VAT_NUMBER_MISSING: [
    "Business customer, no VAT number supplied — outside the scope of UK VAT, reported in Box 6.",
    "Customer confirmed as VAT-registered but the number was not captured on the invoice; treatment unchanged for this period.",
    "Customer is not VAT-registered in their country — consumer treatment is correct.",
    "VAT number requested from the client; filing this period without it and correcting at source.",
  ],
  VAT_NUMBER_INVALID: [
    "VAT number confirmed as deregistered by the customer — consumer treatment is correct.",
    "Customer supplied a corrected number after the period end; treatment unchanged for this filing.",
    "Number rejected by VIES; customer asked for a valid number — consumer treatment stands until one is supplied.",
  ],
  BUYER_COUNTRY_VAT_PREFIX_MISMATCH: [
    "Buyer country confirmed against the contract — the VAT prefix on the invoice is the error.",
    "Customer has establishments in both countries; the coded country is the correct place of supply.",
  ],
  // A2-637 Owner C — the one legitimate reason the card names, and its kin
  CREDIT_NOTE_EXCEEDS_ORIGINAL: [
    "This credit note settles several invoices; checked against each of them — the total is correct.",
    "Checked against the credit note issued — the amount is correct as issued.",
    "The original invoice was split across several rows of the file; together they cover this credit note.",
  ],
  UNCLASSIFIED: [
    "Reviewed against the source document — the current treatment is correct.",
    "Non-standard supply confirmed with the client; keeping the assigned treatment.",
  ],
};

export const presetsFor = (flagType) => [
  ...(BY_FLAG[flagType] || GENERIC),
];

// Dropping a transaction OUT of a filing. A different act from
// acknowledging one: this removes the row from the return, so the
// reasons are about why it should not be there at all — and the
// backlog page makes the operator resolve every one of them before
// the spreadsheet will export.
//
// Each is >= 20 characters, which is the minimum the drop panel
// enforces (higher than the acknowledge path's 10, deliberately: this
// one changes the filed figures).
export const DROP_REASONS = [
  "Duplicate of a transaction already included in this filing.",
  "Test or sample data that should not have been uploaded.",
  "Cancelled order that was never invoiced to the customer.",
  "Belongs to a different entity's filing — uploaded against the wrong client.",
  "Data-entry error at source; correcting the record and re-uploading.",
];

// Resolving a dropped transaction in the backlog. The three resolutions
// mean genuinely different things, so they get different lists —
// offering "not a real sale" reasons under "filed in a later calc"
// would invite the wrong record.
//
// v0.6.12 A2-182 — `another_client` added. Until then a row dropped
// because it was uploaded against the wrong client had no true ending:
// the operator had to call a real sale "not a real sale", or name
// another client's calculation in this client's audit trail. The
// operator's own objection is the point — one mis-assigned order in a
// mixed file is entirely ordinary, and dropping it IS correct; it was
// the RESOLUTION that had nothing honest to say.
export const BACKLOG_REASONS = {
  filed_later: [
    "Included in a later calculation for the correct period.",
    "Re-uploaded with corrected data and filed in the calculation named above.",
    "Held back deliberately and reported in the following return.",
  ],
  not_a_sale: [
    "Test or sample data — not a real sale.",
    "Duplicate row; the original is already filed.",
    "Cancelled order that was never invoiced to the customer.",
    "Created in error by a defect and never represented a real supply.",
  ],
  another_client: [
    "Belongs to another entity in the group; it will be reported on their return.",
    "Uploaded against the wrong client; the sale is real and is theirs, not this entity's.",
    "The supplying entity is a different company; this return is not where it belongs.",
  ],
};

// ---------------------------------------------------------------------
// v0.6.12 A5-27 — pick, don't write, for every remaining audit-trail
// reason box. Operator: "change to dropdowns".
//
// Same rules as everything above: plain sentences, no <TOKEN>
// templates (tokens are what produced A1-61), each one able to stand
// alone in an audit trail, and "Other" kept as the last option so a
// list that cannot express a real case never forces the nearest wrong
// answer.
//
// Every preset is at least as long as its endpoint's backend minimum
// and contains no "<" or ">" — reviewReasonPresets.lengths.test.js
// pins both. The minimum for each list is noted beside it.
//
// Deliberately NOT here: presets that would assert a legal rule (an
// error-size correction threshold, the law on choosing an OSS country
// of identification, a retroactive rate change "by law"). Those were
// proposed and dropped — a canned sentence must not state law the app
// has not cited. Those acts keep the remaining presets plus Other.
// ---------------------------------------------------------------------

// POST /runs/calc/{id}/resolve-art59-category — reason 8..500.
export const ART59_CATEGORY_REASONS = [
  "Kind of service confirmed from the invoice description of what was sold.",
  "Kind of service confirmed from the contract or order with the customer.",
  "Kind of service confirmed with the client from their product listing.",
  "Same service the client sells to other customers, already categorised the same way.",
];

// POST /runs/calc/{id}/reclassify-fx-treatment — reason 8..500.
// Keyed by the treatment picked: the two choices are opposite facts.
export const FX_TREATMENT_REASONS = {
  inclusive: [
    "The price charged already included VAT; no VAT was added on top on the invoice.",
    "The marketplace paid out the full price; the VAT is taken out of the amount received.",
    "The client confirmed the customer was not sent a new invoice.",
  ],
  reinvoiced: [
    "A corrected invoice was issued adding VAT on top of the original price.",
    "A credit note cancelled the original invoice and a new invoice charged VAT on top.",
    "The customer agreed to pay the VAT in addition to the original price.",
  ],
};

// POST /runs/calc/{id}/amend-rate-category — reason 10..2000.
export const RATE_CATEGORY_REASONS = [
  "Category checked against the product description on the invoices.",
  "The client confirmed the rate category for this product in this country.",
  "The same product is already categorised this way on the client's other sales.",
  "Category taken from the marketplace's own tax setting for this product.",
];

// POST /runs/calc/{id}/assign-refund-channel — reason 10..2000.
export const REFUND_CHANNEL_REASONS = [
  "The credit note quotes the original invoice, which was reported through this channel.",
  "Original sale found in an earlier period's file; the refund follows the same channel.",
  "The client confirmed which sale this refund reverses.",
  "Only this channel fits the customer type and country shown on the credit note.",
  "The original sale was filed before this app was used; channel taken from that filed return.",
];

// POST /runs/calc/{id}/resolve-service-country — reason 8..500.
export const SERVICE_COUNTRY_REASONS = [
  "Work location taken from the job sheet or repair order.",
  "The invoice states the address where the work was carried out.",
  "The client confirmed where the work was physically done.",
  "The goods were sent to the client's workshop in this country for the work.",
  "The work was done on site at the customer's premises in this country.",
];

// A2-582 — POST /runs/calc/{id}/resolve-location-country, per field (8..500).
export const LOCATION_COUNTRY_REASONS = {
  service_country: [
    ...SERVICE_COUNTRY_REASONS,
    "The contract states where the service is performed.",
  ],
  property_country: [
    "The property's address is on the invoice.",
    "The contract names the property and its address.",
    "The client confirmed where the property is.",
  ],
  event_country: [
    "The event's venue is on the ticket or the invoice.",
    "The event listing names the venue.",
    "The client confirmed where the event took place.",
  ],
  handover_country: [
    "The hire agreement names where the vehicle or goods were handed over.",
    "The delivery note shows the handover address.",
    "The client confirmed where the handover took place.",
  ],
  underlying_country: [
    "The underlying supply's invoice shows where it was made.",
    "The agency contract names the country of the underlying supply.",
    "The client confirmed where the underlying supply took place.",
  ],
};

// POST /runs/calc/{id}/amend-vat-number — reason 10..2000.
export const VAT_NUMBER_AMEND_REASONS = [
  // A2-531 — a number missing from the upload, now amendable
  "The customer's VAT number was missing from the upload; taken from their invoice or account details.",
  "Typing error in the uploaded file; correct number taken from the customer's invoice.",
  "The customer supplied their correct VAT number after the sale.",
  "The country letters were missing or wrong in the upload; the number itself is unchanged.",
  "The customer re-registered; using the number that was valid on the sale date.",
  "Correct number copied from the customer's account details on the marketplace.",
];

// POST /runs/calc/{id}/resolve-customer-establishment — reason 8..500.
// Keyed by the answer (ESTABLISHMENT_ANSWERS in
// CustomerEstablishmentResolver.jsx).
export const ESTABLISHMENT_REASONS = {
  ESTABLISHED_HERE: [
    "The customer's registered office and invoice address are in that country.",
    "The customer confirmed in writing that they are established in that country.",
    "The company register shows the customer is incorporated in that country.",
  ],
  ESTABLISHED_ELSEWHERE: [
    "The customer's registered office is in another country; this VAT number is only a local registration.",
    "The customer confirmed in writing that they have no business presence in that country.",
    "The contract and invoice address show the customer is based in another country.",
  ],
};

// POST /runs/calc/{id}/resolve-property-letting (A2-484) — reason >= 8.
export const LETTING_REASONS = {
  LETTING: [
    "The invoice is for rent under a lease of the property.",
    "The invoice is for renting rooms, a flat or an office in the property.",
    "The invoice is for renting parking spaces at the property.",
  ],
  NOT_LETTING: [
    "The invoice is for building, repair or maintenance work on the property.",
    "The invoice is for agent, surveyor, architect or management services for the property.",
    "The invoice is for cleaning or other services at the property, not for its use.",
  ],
};

// POST /admin/amendments/{id}/resolve — no_corrective_reason >= 50.
export const AMENDMENT_NO_CORRECTION_REASONS = [
  "The change takes effect after this period ended, so the filed return is not affected.",
  "None of the sales in the filed period were affected by the change, so no figure on the return moves.",
  "The amendment was opened in error: the filed figures were already correct when checked.",
  "The client's tax adviser confirmed in writing that the filed return does not need correcting.",
];

// POST /admin/amendments/{id}/submission — reason >= 50.
export const AMENDMENT_SUBMISSION_REASONS = [
  "Corrected return submitted through the authority's online portal using the corrective calculation's figures.",
  "Corrected return submitted by the client's tax agent using the corrective calculation's figures.",
  "Error-correction notice sent to the authority with the figures from the corrective calculation.",
  "Correction included in the next periodic return using the corrective calculation's figures.",
];

// POST /admin/amendments/{id}/notes — text >= 1. A running log that
// often carries a phone or letter reference, so Other matters most here.
export const AMENDMENT_NOTES = [
  "Waiting for the client to confirm the corrected figures.",
  "Asked the client's tax adviser whether a correction is needed.",
  "Contacted the authority and waiting for their reply.",
  "Corrective calculation is being prepared.",
];

// POST /admin/rates/acks/{id} — reason >= 50. Keyed by decision.
export const RATE_ACK_REASONS = {
  no_amendment_needed: [
    "Checked the authority's own rate list: the rate on file is correct and the reported change does not apply.",
    "The new rate starts after every affected filing's period, so none of the filed returns change.",
    "The reported difference is an error in the published source; the authority's own publication confirms our rate.",
    "No sales in this rate category in any affected filing, so no filed figure changes.",
  ],
  amendment_required: [
    "Checked the authority's publication: the new rate applied during periods already filed, so those returns need correcting.",
  ],
};

// POST /runs/calc/{id}/coverage/acknowledge — note 10..1000. Keyed by
// the gap's reason_code (engine/registration_coverage.py); `_default`
// covers any code without its own list. "The registration exists but
// is not in the profile yet" is deliberately absent: the honest fix
// for that is adding the registration, not acknowledging the gap.
export const COVERAGE_ACK_REASONS = {
  LOCAL_REGISTRATION_MISSING: [
    "Covered by a group VAT registration in this country held by another group company.",
    "Registration has been applied for; the authority has not issued the number yet.",
    "The client's tax adviser confirmed no registration is needed in this country.",
  ],
  IOSS_REGISTRATION_MISSING: [
    "Imports are declared under an intermediary's import-scheme number, not the client's own.",
    "Import-scheme registration has been applied for; the number has not been issued yet.",
  ],
  OSS_REGISTRATION_MISSING: [
    "One Stop Shop registration has been applied for; the number has not been issued yet.",
    "The client's tax adviser confirmed these sales are reported on local returns instead.",
  ],
  NON_UNION_OSS_REGISTRATION_MISSING: [
    "One Stop Shop registration has been applied for; the number has not been issued yet.",
    "The client's tax adviser confirmed these sales are reported on local returns instead.",
  ],
  OSS_MSI_ESTABLISHMENT_MISMATCH: [
    "Country of identification confirmed with the client's tax adviser.",
  ],
  _default: [
    "The client's tax adviser confirmed that filing without this registration is correct.",
  ],
};

export const coveragePresetsFor = (reasonCode) => [
  ...(COVERAGE_ACK_REASONS[reasonCode] || COVERAGE_ACK_REASONS._default),
];

// POST /runs/calc/{id}/signoff reconciliation_override.reason — the
// backend only asks for non-empty; the panel asks for 20 when typed.
export const RECON_OVERRIDE_REASONS = [
  "The difference is caused by rounding in the marketplace's figures; checked line by line.",
  "Figures checked by hand against the source report; the return totals are correct.",
  "The difference comes from one known row that will be corrected on the next return.",
  "Reviewed with the client's accountant, who approved filing these figures.",
];

// A2-536 (found 2026-10-25 by the Antigravity UX review, U2): calc b3bc6d82
// was signed off over a 42,889.96 difference with the "rounding" reason. That
// reason fits only a difference rounding can make, so it is offered only when
// every failed check is within one unit of currency.
export const ROUNDING_LIMIT = 1;
export const reconOverridePresets = (failures = []) => {
  const big = (failures || []).some((f) => Math.abs(Number(f?.delta) || 0) > ROUNDING_LIMIT);
  return big ? RECON_OVERRIDE_REASONS.filter((r) => !/rounding/i.test(r)) : [...RECON_OVERRIDE_REASONS];
};

// POST /runs/calc/{id}/submission note — optional, <= 2000. Blank stays
// a real choice: no note at all.
export const SUBMISSION_NOTES = [
  "Submitted by us on the client's behalf.",
  "Submitted by the client directly; their confirmation was forwarded to us.",
  "Submitted after the filing deadline.",
  "Payment made at the same time as the return was submitted.",
];

export default presetsFor;

// A2-592 — why the operator relies on evidence for a customer whose VAT
// number the EU check rejects, per kind of evidence (IR 282/2011 Arts 17-18).
export const BUSINESS_EVIDENCE_REASONS = {
  // A2-625 (operator 2026-10-31) — evidence held, kind not named
  EVIDENCE_HELD: [
    "Contracts and billing addresses show these customers are businesses.",
    "Evidence the customer is in business is held on file.",
  ],
  CERTIFICATE_OF_STATUS: [
    "The customer's tax authority certified that it is in business.",
    "The certificate covers the invoice dates and names this customer.",
  ],
  VALID_ON_INVOICE_DATE: [
    "An earlier EU check confirmed the number on the invoice dates.",
    "The EU check's consultation record for those dates is on file.",
  ],
  NUMBER_NEWLY_ISSUED: [
    "The customer applied for a VAT number and sent proof of the application.",
    "The customer's tax authority confirmed the application is being processed.",
  ],
};

// A2-592 — why evidence is withdrawn.
export const EVIDENCE_WITHDRAW_REASONS = [
  "Information has come to light that the customer is not in business.",
  "The evidence was recorded in error.",
  "The customer's tax authority withdrew the certificate.",
];

// A1-104 — "Where does the customer belong?" and "What was sold?" (the
// evidence HMRC Notice 741A s.6.3 asks for: a VAT number, or a contract and
// a billing address).
export const CUSTOMER_COUNTRY_REASONS = [
  "Customer's contract and billing address checked.",
  "Customer's VAT number checked against its country.",
  "Customer confirmed where it is established.",
];
export const SOLD_AS_REASONS = [
  "The invoice describes what was sold.",
  "The event listing or ticket shows how it was attended.",
  "The client confirmed what was sold.",
];

// A2-586 — clearing an earlier answer that no longer fits the row
export const CLEAR_DECISION_REASONS = [
  "The row changed since the earlier answer; deciding it again.",
  "The earlier answer was given on different data.",
];
